import { useState, useEffect } from 'react';
import { getStudentGradeHistory, getEvaluationPeriods, type GradeHistoryEntry } from '../../lib/db';
import type { Student, EvaluationPeriodConfig } from '../../types';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface GradeHistoryProps {
  student: Student;
  sectionNames?: Record<string, string>;
}

export function GradeHistory({ student, sectionNames: propSectionNames }: GradeHistoryProps) {
  const [history, setHistory] = useState<GradeHistoryEntry[]>([]);
  const [periods, setPeriods] = useState<EvaluationPeriodConfig[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getStudentGradeHistory(student.id, student.courseId),
      getEvaluationPeriods(),
    ]).then(([h, p]) => {
      setHistory(h);
      setPeriods(p);
    }).finally(() => setLoading(false));
  }, [student.id, student.courseId]);

  if (loading) return <div className="text-sm text-gray-400 py-4">Cargando historial...</div>;
  if (!history.length) return <div className="text-sm text-gray-400 py-4">Sin historial de notas</div>;

  // Build per-section data
  const sectionIds = new Set<string>();
  const sectionNames: Record<string, string> = propSectionNames || {};
  history.forEach(h => {
    Object.keys(h.grades).forEach(sId => {
      sectionIds.add(sId);
    });
  });

  const getPeriodLabel = (key: string) => periods.find(p => p.key === key)?.label || key;

  // Extract global grades per section per period
  // Only show sections that have a known name
  const sections = Array.from(sectionIds).filter(sId => sectionNames[sId]);
  
  const getGlobalNum = (entry: GradeHistoryEntry, sectionId: string): number | null => {
    const s = entry.grades[sectionId];
    if (!s?.global) return null;
    const n = parseFloat(s.global);
    return isNaN(n) ? null : n;
  };

  const getTrend = (vals: (number | null)[]) => {
    const nums = vals.filter((v): v is number => v !== null);
    if (nums.length < 2) return 'neutral';
    return nums[nums.length - 1] > nums[0] ? 'up' : nums[nums.length - 1] < nums[0] ? 'down' : 'neutral';
  };

  const getGradeColor = (val: number | null) => {
    if (val === null) return 'text-gray-300';
    return val >= 5 ? 'text-emerald-600' : 'text-red-600';
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-indigo-500" />
        Evolución de Notas — {student.firstName} {student.lastName}
      </h3>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-3 text-gray-500 font-medium">Asignatura</th>
              {history.map(h => (
                <th key={h.evaluationPeriod} className="text-center py-2 px-3 text-gray-500 font-medium">
                  {getPeriodLabel(h.evaluationPeriod)}
                </th>
              ))}
              <th className="text-center py-2 px-3 text-gray-500 font-medium">Tendencia</th>
            </tr>
          </thead>
          <tbody>
            {sections.map(sId => {
              const vals = history.map(h => getGlobalNum(h, sId));
              const trend = getTrend(vals);
              return (
                <tr key={sId} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-2 px-3 text-gray-700 font-medium truncate max-w-[200px]">
                    {sectionNames[sId] || sId.slice(0, 8)}
                  </td>
                  {vals.map((v, i) => (
                    <td key={i} className="text-center py-2 px-3">
                      <span className={`font-bold text-lg ${getGradeColor(v)}`}>
                        {v !== null ? v : '—'}
                      </span>
                    </td>
                  ))}
                  <td className="text-center py-2 px-3">
                    {trend === 'up' && <TrendingUp className="w-5 h-5 text-emerald-500 mx-auto" />}
                    {trend === 'down' && <TrendingDown className="w-5 h-5 text-red-500 mx-auto" />}
                    {trend === 'neutral' && <Minus className="w-5 h-5 text-gray-400 mx-auto" />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
